import React, { useState, useMemo, JSX } from 'react';
import { FaLaptopCode, FaShieldAlt, FaTools, FaRobot } from 'react-icons/fa';

interface Service {
  icon: JSX.Element;
  title: string;
  description: string;
  details: string;
}

export default function Services() {
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  // Memoize the services array to prevent unnecessary re-renders
  const services: Service[] = useMemo(() => [
    {
      icon: <FaLaptopCode className="text-4xl text-gray-600" />,
      title: 'UI & UX Design',
      description: 'Custom and modern web designs tailored to your business needs.',
      details: 'We create visually appealing, user-friendly, and responsive web designs that resonate with your target audience. Our designs focus on user experience, ensuring an intuitive and engaging interface for visitors.'
    },
    {
      icon: <FaLaptopCode className="text-4xl text-gray-600" />,
      title: 'Web Development',
      description: 'Building fast, secure, and scalable web applications.',
      details: 'Our team of experts develops custom websites and web applications using the latest technologies. From e-commerce platforms to complex web solutions, we ensure your website runs smoothly and efficiently.'
    },
    {
      icon: <FaShieldAlt className="text-4xl text-gray-600" />,
      title: 'Consulting',
      description: 'Expert advice to guide your business through digital transformations.',
      details: 'Our consultants provide expert guidance in optimizing your business operations, IT infrastructure, and overall technology strategy to improve your business efficiency and growth.'
    },
    {
      icon: <FaTools className="text-4xl text-gray-600" />,
      title: 'Maintenance',
      description: 'Ongoing website maintenance and support to keep your business running smoothly.',
      details: 'We offer continuous monitoring, updates, and support for your website, ensuring optimal performance, security, and user satisfaction over time. Our maintenance services help you avoid downtime and technical issues.'
    },
    {
      icon: <FaRobot className="text-4xl text-gray-600" />,
      title: 'Process Automation',
      description: 'Automate repetitive tasks and enhance operational efficiency.',
      details: 'Our process automation solutions streamline business operations, reduce human error, and improve productivity. We leverage cutting-edge technologies like RPA and AI to automate workflows, saving you time and costs.'
    }
  ], []);

  return (
    <section className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Visual */}
      <div 
        className="lg:w-1/2 h-64 lg:h-auto bg-cover bg-center" 
        style={{ backgroundImage: "url('images/web-design.jpg')" }}
      >
        <div className="bg-black bg-opacity-30 h-full w-full"></div>
      </div>

      {/* Right Side - Content */}
      <div className="lg:w-1/2 bg-white flex flex-col justify-center my-32 p-8">
        <div className="text-center mb-12">
          <h2 className="text-5xl font-bold mb-4 relative inline-block text-gray-800">
            Our Services
            <img 
              src="/images/unterstrch.png"
              alt="Underline" 
              className="absolute left-1/2 transform -translate-x-3/4 -bottom-1 w-3/4 opacity-80 z-20"
            />
          </h2>
          <p className="text-xl text-gray-600">
            Helping businesses navigate the digital landscape with innovative IT solutions that push the boundaries of technology.
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-white shadow-lg rounded-xl p-6 text-center hover:shadow-2xl transition-all transform hover:scale-105 cursor-pointer relative"
              onClick={() => setSelectedService(service)}
            >
              <div className="mb-4">{service.icon}</div>
              <h3 className="text-2xl font-semibold text-gray-800 mb-2 relative inline-block">
                {service.title}
                <img 
                  src="/images/unterstrch.png"
                  alt="Underline" 
                  className="absolute left-1/2 transform -translate-x-3/4 -bottom-1 w-3/4 opacity-80 z-20"
                />
              </h3>
              <p className="text-gray-600">{service.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Selected Service Details Modal */}
      {selectedService && (
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-3xl w-full animate__animated animate__zoomIn">
            <h3 className="text-3xl font-bold text-gray-800 mb-4">{selectedService.title}</h3>
            <p className="text-lg text-gray-600 mb-6">{selectedService.details}</p>
            <button
              className="bg-gray-700 text-white px-6 py-3 rounded-lg text-lg hover:bg-gray-200 transition"
              onClick={() => setSelectedService(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
